import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Cpu, Leaf, Activity, ArrowRight, ArrowUpRight } from "lucide-react";
import Hero from "@/components/Hero";
import { Partners } from "@/components/Marketing";
import { Reveal, Chapter, SectionTitle } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";

const cards = (fr) => [
  { to: "/projet", icon: Building2, key: "nav.about", d: fr ? "Vision, mission et modèle BOOT sur 40 ans." : "Vision, mission and the 40-year BOOT model." },
  { to: "/technique", icon: Cpu, key: "nav.technical", d: fr ? "300 MWc, modules bifaciaux, 180 ha à Paoskoto." : "300 MWc, bifacial modules, 180 ha in Paoskoto." },
  { to: "/impact", icon: Leaf, key: "nav.esg", d: fr ? "9,9 Mt CO₂ évitées, agrivoltaïque, 1 000 jeunes formés." : "9.9Mt CO₂ avoided, agrivoltaics, 1,000 youth trained." },
  { to: "/suivi", icon: Activity, key: "nav.monitoring", d: fr ? "Dashboard temps réel, carte interactive, avancement." : "Real-time dashboard, interactive map, progress." },
];

export default function Home() {
  const { t, lang } = useLang();
  const fr = lang === "fr";
  return (
    <>
      <Hero />

      <section className="py-24 sm:py-32 bg-slate-50 noise-bg">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Chapter>{fr ? "Explorer le projet" : "Explore the project"}</Chapter>
          <SectionTitle>{fr ? "Un projet d'infrastructure intégré, section par section" : "An integrated infrastructure project, section by section"}</SectionTitle>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards(fr).map((c, i) => (
              <Reveal key={c.to} delay={i * 0.06}>
                <Link
                  to={c.to}
                  data-testid={`home-card-${c.to.replace("/", "")}`}
                  className="group block h-full bg-white rounded-2xl border border-slate-200 p-8 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center">
                      <c.icon className="w-5 h-5 text-sky-700" />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-slate-300 group-hover:text-sky-700 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-[color,transform] duration-200" />
                  </div>
                  <div className="font-display text-xl text-slate-900 mt-6">{t(c.key)}</div>
                  <p className="text-slate-600 text-sm mt-2 leading-relaxed">{c.d}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Partners />

      <section className="relative py-24 sm:py-32 bg-sky-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #0ea5e9 0%, transparent 55%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight max-w-3xl mx-auto text-balance"
          >
            {fr ? "Rejoignez un projet énergétique structurant pour le Sénégal" : "Join a landmark energy project for Senegal"}
          </motion.h2>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/projet" data-testid="home-cta-discover" className="group inline-flex items-center justify-center gap-2 rounded-full bg-secondary px-7 py-3.5 font-medium hover:bg-green-700 active:scale-95 transition-[background-color,transform] duration-200">
              {fr ? "Découvrir le projet" : "Explore the project"}<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
            <Link to="/contact" data-testid="home-cta-contact" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-7 py-3.5 font-medium hover:bg-white/10 active:scale-95 transition-[background-color,transform] duration-200">
              {t("hero.cta2")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
